import {
  AnchorButton,
  FooterColumn,
  FooterDD,
  FooterDL,
  FooterDT,
  HeartIcon,
  Footer as UIFooter,
} from '@instabox/consumer-web-ui'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ENV_CONFIG } from '../../constants/envConfig'
import { GetMarketByIdQuery } from '../../graphql/__generated__/types'
import { localeToMarketCode } from '../../utils'
import formatOpeningHours, {
  formatSpecialOpeningHours,
  FormattedOpeningHour,
} from '../../utils/formatOpeningHours'
import OpeningHour from '../OpeningHour'
import MarketSelect from './MarketSelect'

export type FooterProps = GetMarketByIdQuery['marketById']

export const Footer: FC<FooterProps> = ({
  opening_hours: openingHours = [],
  special_opening_hours: specialOpeningHours = [],
  legal_entity = {},
  email,
  phone,
  phone_caption,
  sms,
  facebook_link,
  instagram_link,
  linkedin_link,
}) => {
  const [formattedOpeningHours, setFormattedOpeningHours] = useState<FormattedOpeningHour[]>([])
  const [formattedSpecialOpeningHours, setFormattedSpecialOpeningHours] = useState<
    FormattedOpeningHour[]
  >([])
  const { locale } = useRouter()

  const { formatMessage } = useIntl()

  useEffect(() => {
    setFormattedOpeningHours(
      formatOpeningHours({
        openingHours,
        locales: locale,
      }),
    )
    setFormattedSpecialOpeningHours(
      formatSpecialOpeningHours({
        openingHours: specialOpeningHours,
        locales: locale,
      }),
    )
  }, [openingHours, specialOpeningHours, locale])

  const market = useMemo(() => localeToMarketCode(locale), [locale])

  return (
    <div className="mt-auto">
      <UIFooter>
        <FooterColumn
          className="col-6 col-md-5"
          heading={formatMessage({ id: 'general.customer_service' })}>
          {sms && (
            <FooterDL>
              <FooterDT>{formatMessage({ id: 'general.message_us' })}</FooterDT>
              <FooterDD>
                <a href={`sms:${sms}`}>{sms}</a>
              </FooterDD>
            </FooterDL>
          )}
          {phone && (
            <FooterDL>
              <FooterDT>{formatMessage({ id: 'general.call_us' })}</FooterDT>
              <FooterDD>
                <a href={`tel:${phone}`}>{phone}</a> {phone_caption}
              </FooterDD>
            </FooterDL>
          )}
          {formattedOpeningHours.length > 0 && (
            <FooterDL>
              <FooterDT>{formatMessage({ id: 'general.opening_hours' })}</FooterDT>
              {formattedOpeningHours.map((openingHour, index) => (
                <FooterDD key={index}>
                  <OpeningHour {...openingHour} />
                </FooterDD>
              ))}
            </FooterDL>
          )}
          {formattedSpecialOpeningHours.length > 0 && (
            <FooterDL>
              <FooterDT>{formatMessage({ id: 'general.special_opening_hours' })}</FooterDT>
              {formattedSpecialOpeningHours.map((openingHour, index) => (
                <FooterDD key={index}>
                  <OpeningHour {...openingHour} />
                </FooterDD>
              ))}
            </FooterDL>
          )}
          {email && (
            <FooterDL>
              <FooterDT>{formatMessage({ id: 'general.email_us' })}</FooterDT>
              <FooterDD>
                <a href={`mailto:${email}`}>{email}</a>
              </FooterDD>
            </FooterDL>
          )}
        </FooterColumn>
        <FooterColumn className="col-6 col-md-7">
          <div className="row">
            <FooterColumn
              heading={formatMessage({ id: 'general.address' })}
              className="col-12 col-md-6">
              <FooterDL>
                <FooterDD italic fontWeight="medium">
                  {legal_entity?.name}
                </FooterDD>
                <FooterDD>
                  {legal_entity?.address}
                  <br />
                  {legal_entity?.postal_code} {legal_entity?.city}
                </FooterDD>
              </FooterDL>
              <FooterDD>
                {formatMessage(
                  { id: 'general.organization_nr' },
                  { organization_nr: legal_entity?.organization_number },
                )}
              </FooterDD>
              <FooterDL>
                <FooterDD>
                  <a href="https://instabee.com/general-terms-conditions/" target="_blank">
                    {formatMessage({ id: 'general.legal.terms-and-condtions' })}
                  </a>
                </FooterDD>
                <FooterDD>
                  <a href="https://instabee.com/external-privacy-notice/" target="_blank">
                    {formatMessage({ id: 'general.legal.privacy-policy' })}
                  </a>
                </FooterDD>
                <FooterDD>
                  <a href="https://instabee.com/code-of-conducts/" target="_blank">
                    {formatMessage({ id: 'general.legal.code-of-conduct' })}
                  </a>
                </FooterDD>
                <FooterDD>
                  <a href="https://instabee.com/whistleblowing/" target="_blank">
                    {formatMessage({ id: 'general.legal.whistleblowing' })}
                  </a>
                </FooterDD>
                {market === 'no' && (
                  <>
                    <FooterDD>
                      <Link href="/legal/transparency-statement">
                        {formatMessage({ id: 'general.legal.transparency-statement' })}
                      </Link>
                    </FooterDD>
                    <FooterDD>
                      <Link
                        href={`${ENV_CONFIG.DIRECTUS_URL}/assets/b1732169-d112-43a3-b3ad-f2bb6a2b65b0`}
                        target="_blank">
                        {formatMessage({ id: 'general.legal.arp-report' })}
                      </Link>
                    </FooterDD>
                  </>
                )}
                <FooterDD>
                  <Link href="/cookies">{formatMessage({ id: 'general.cookies' })}</Link>
                </FooterDD>
              </FooterDL>
            </FooterColumn>
            <FooterColumn
              heading={formatMessage({ id: 'general.follow' })}
              className="col-12 col-md-6">
              <FooterDL>
                {facebook_link && (
                  <FooterDD>
                    <a href={facebook_link}>{formatMessage({ id: 'general.socials.facebook' })}</a>
                  </FooterDD>
                )}
                {instagram_link && (
                  <FooterDD>
                    <a href={instagram_link}>
                      {formatMessage({ id: 'general.socials.instagram' })}
                    </a>
                  </FooterDD>
                )}
                {linkedin_link && (
                  <FooterDD>
                    <a href={linkedin_link}>{formatMessage({ id: 'general.socials.linkedin' })}</a>
                  </FooterDD>
                )}
              </FooterDL>
              {market === 'se' && (
                <FooterDL>
                  <FooterDD>
                    <Link href="/app" passHref legacyBehavior>
                      <AnchorButton
                        icon={<HeartIcon backgroundColor="var(--color-really-red)" />}
                        variant="ghost"
                        size="small">
                        <FormattedMessage id="general.download_app_button" />
                      </AnchorButton>
                    </Link>
                  </FooterDD>
                </FooterDL>
              )}
            </FooterColumn>
          </div>
        </FooterColumn>
        <FooterColumn className="col-12" withSeparator>
          <div className="row justify-content-center">
            <FooterColumn className="col-10 col-sm-8 col-md-4 col-lg-3">
              <MarketSelect />
            </FooterColumn>
          </div>
        </FooterColumn>
      </UIFooter>
    </div>
  )
}

export default Footer
